import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { reviewTaskKeys } from "@/lib/query";
import type { ReviewResponseInput } from "@/schemas/review-tasks";

export interface ReviewTaskSummary {
  reviewerId: string;
  reviewId: string;
  relationshipKey: string;
  relationshipName: string;
  subjectPersonId: string;
  subjectName: string;
  roleName: string;
  roleLevelName: string | null;
  cycleId: string;
  cycleName: string;
  cycleStatusKey: string;
  dueOn: string;
  statusKey: string;
  answered: number;
  total: number;
  isOverdue: boolean;
}

/** One finished review about you, from a round that has closed. */
export interface ReviewResultSummary {
  reviewId: string;
  cycleId: string;
  cycleName: string;
  roleName: string;
  roleLevelName: string | null;
  periodStart: string;
  periodEnd: string;
  closedAt: string | null;
}

export interface TeamReviewResultSummary extends ReviewResultSummary {
  subjectName: string;
  managerSignedAt: string | null;
  employeeSignedAt: string | null;
}

export interface ReviewTaskCriterion {
  questionId: string;
  competencyId: string;
  name: string;
  description: string | null;
  categoryName: string | null;
  categoryColor: string | null;
  isRequired: boolean;
  visibility: "all_readers" | "people_ops";
  ordinal: number | null;
  comment: string | null;
  isSensitive: boolean;
  /** What each rung means for this role and level, keyed by ordinal. */
  expectations: Record<number, string>;
}

export interface ReviewTaskDetail extends ReviewTaskSummary {
  instructions: string | null;
  scale: { ordinal: number; name: string; description: string | null }[];
  criteria: ReviewTaskCriterion[];
}

async function failure(res: Response, fallback: string): Promise<Error> {
  const json = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return new Error(typeof json?.error === "string" && json.error ? json.error : fallback);
}

export function useMyReviewTasks(enabled = true) {
  return useQuery({
    queryKey: reviewTaskKeys.mine,
    queryFn: async (): Promise<ReviewTaskSummary[]> => {
      const res = await fetch("/api/me/review-tasks");
      if (!res.ok) throw await failure(res, "Failed to load your reviews");
      return (await res.json()).data;
    },
    enabled,
  });
}

/**
 * Your own results from closed rounds. Separate query from the task list
 * because they answer different questions — what you owe, and what came back.
 */
export function useMyReviewResults(enabled = true) {
  return useQuery({
    queryKey: reviewTaskKeys.myResults,
    queryFn: async (): Promise<ReviewResultSummary[]> => {
      const res = await fetch("/api/me/review-results");
      if (!res.ok) throw await failure(res, "Failed to load your results");
      return (await res.json()).data;
    },
    enabled,
  });
}

/** Closed-round reviews of the people you manage — where sign-off is found. */
export function useTeamReviewResults() {
  return useQuery({
    queryKey: reviewTaskKeys.teamResults,
    queryFn: async (): Promise<TeamReviewResultSummary[]> => {
      const res = await fetch("/api/me/team-review-results");
      if (!res.ok) throw await failure(res, "Failed to load your team's reviews");
      return (await res.json()).data;
    },
  });
}

/**
 * The round has closed, so this review can no longer be opened to answer.
 *
 * A distinct type rather than a message to match on: the page renders a
 * finished-round state for this and a genuine error for everything else, and
 * matching on wording breaks the moment the wording is edited.
 */
export class ReviewRoundClosedError extends Error {}

export function useReviewTask(reviewerId: string | null) {
  return useQuery({
    queryKey: reviewTaskKeys.detail(reviewerId ?? ""),
    queryFn: async (): Promise<ReviewTaskDetail> => {
      const res = await fetch(`/api/review-tasks/${reviewerId}`);
      if (res.status === 409) {
        throw new ReviewRoundClosedError((await failure(res, "This round has closed.")).message);
      }
      if (!res.ok) throw await failure(res, "Failed to load this review");
      return (await res.json()).data;
    },
    enabled: Boolean(reviewerId),
    // The form seeds its cards from a fetch made on THIS visit (see
    // ReviewTaskForm), so a cached copy younger than staleTime must still
    // refetch on the way in, or the form would wait for a fetch never made.
    refetchOnMount: "always",
  });
}

/**
 * Save one answer.
 *
 * Deliberately quiet: this fires as the reviewer works, so a toast per rating
 * would be a stream of confirmations nobody asked for. Only a FAILURE speaks,
 * because that is the case where the reviewer would otherwise lose something.
 */
/**
 * Identifies an in-flight answer save for one review task, so the form can
 * tell on its way back in that a save from its last visit has not landed yet.
 */
export const saveReviewResponseKey = (reviewerId: string) =>
  [...reviewTaskKeys.detail(reviewerId), "save-response"] as const;

export function useSaveReviewResponse(reviewerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: saveReviewResponseKey(reviewerId),
    mutationFn: async ({
      questionId,
      input,
    }: {
      questionId: string;
      input: ReviewResponseInput;
    }) => {
      const res = await fetch(`/api/review-tasks/${reviewerId}/responses/${questionId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await failure(res, "Couldn't save that answer");
      return res.json();
    },
    /*
     * The task itself is invalidated too, not only the list. The form keeps
     * each answer in local state seeded from this query, so a detail left
     * stale (30s staleTime) came back on the next visit with the comments from
     * BEFORE the visit — saved on the server, missing on screen, and then
     * overwritten as soon as the reviewer touched the card again.
     *
     * Returned, so the mutation stays pending until the refetch has landed:
     * the form waits on exactly that before seeding its cards.
     */
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: reviewTaskKeys.mine }),
        queryClient.invalidateQueries({ queryKey: reviewTaskKeys.detail(reviewerId) }),
      ]),
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useSubmitReviewTask(reviewerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    /**
     * The one unsigned note, if they wrote one, travels WITH the hand-in.
     *
     * It is not saved as it is typed: a server-side draft of an anonymous note
     * is a row that says who is writing it. Sending it here also gives
     * "one each" for free, because handing in happens once.
     */
    mutationFn: async (anonymousComment?: { body: string; sentimentKey: string } | null) => {
      const res = await fetch(`/api/review-tasks/${reviewerId}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(anonymousComment ? { anonymousComment } : {}),
      });
      if (!res.ok) throw await failure(res, "Couldn't hand this in");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewTaskKeys.all });
      toast.success("Review handed in");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}
